import type { CityConfig } from "../city-template/types";

export const elkGroveConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  lastModified: "2026-06-14",
  city: "Elk Grove",
  state: "California",
  stateAbbr: "CA",
  slug: "elk-grove",
  route: "/elk-grove",
  title: "Emergency Passport Elk Grove, CA — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Elk Grove, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Sacramento County travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Elk Grove, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Elk Grove and Sacramento County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Elk%20Grove%20California%20suburban%20landscape%20at%20golden%20hour%20with%20modern%20residential%20neighborhoods%20and%20community%20parks%2C%20tree-lined%20streets%20with%20beautiful%20California%20oaks%2C%20Sacramento%20Valley%20farmland%20transitioning%20to%20suburbs%2C%20warm%20sunset%20light%20over%20Northern%20California%2C%20professional%20architectural%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20serene%20family-friendly%20suburban%20environment&width=1800&height=700&seq=elk-grove-hero-001&orientation=landscape",
  heroImageAlt: "Elk Grove California suburban landscape at golden hour with modern neighborhoods and community parks",
  geoPosition: "38.4088;-121.3716",
  geoPlacename: "Elk Grove, California",
  regionalAgency: {
    name: "San Francisco Passport Agency",
    address: "95 Hawthorne St Suite 200, San Francisco, CA 94105",
    distance: "Located in downtown SF — approximately 90–100 minutes from Elk Grove via I-5 & I-80",
  },
  nearbyCitiesZips: [
    { name: "Elk Grove", zips: "95624, 95757, 95758" },
    { name: "Sacramento", zips: "95814–95835" },
    { name: "Galt", zips: "95632" },
    { name: "Lodi", zips: "95240, 95242" },
    { name: "Rancho Cordova", zips: "95670, 95742" },
    { name: "Folsom", zips: "95630" },
    { name: "Roseville", zips: "95661, 95678, 95747" },
    { name: "Citrus Heights", zips: "95610, 95611, 95621" },
    { name: "Stockton", zips: "95201–95212" },
    { name: "Davis", zips: "95616, 95618" },
    { name: "Woodland", zips: "95695, 95776" },
    { name: "Vacaville", zips: "95687, 95688" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Sacramento County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Sacramento", route: "/sacramento" },
    { name: "San Francisco", route: "/san-francisco" },
    { name: "San Jose", route: "/san-jose" },
    { name: "Stockton", route: "/stockton" },
    { name: "Roseville", route: "/roseville" },
    { name: "Fresno", route: "/fresno" },
    { name: "Oakland", route: "/oakland" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Elk Grove remotely via secure courier. We do not have a walk-in office in Elk Grove — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Sacramento County travelers.",
  fpcOfficeAddress: "400 S El Camino Real, San Mateo, CA 94402",
  fpcOfficeDistance: "Serving all of Sacramento County",
  serviceAreaIntro: "From the Laguna West neighborhood to the Sheldon area, from the Old Town to the Franklin district — Fast Passport Center serves the entire Elk Grove community. We regularly help travelers from Sacramento, Galt, Lodi, and Rancho Cordova get their passports fast.",
  nearbyCitiesIntro: "We serve all Elk Grove neighborhoods and nearby Sacramento County cities including Sacramento, Galt, Rancho Cordova, Folsom, and Citrus Heights. If you are in the Sacramento metro area, we have you covered.",
  howItWorksIntro: "Most Elk Grove travelers complete everything online and ship documents overnight. No need to drive to San Francisco or wait in line at the regional agency.",
  mistakesIntro: "With Sacramento International Airport (SMF) serving the capital region, Elk Grove travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Elk Grove and Sacramento County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Elk Grove? Here are the most common ones we hear from Sacramento County travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  dmvWebsite: "https://www.dmv.ca.gov",
  dmvLabel: "California DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Sacramento County international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Sophia N.",
      location: "Elk Grove, CA",
      rating: 5,
      text: "My family cruise to Mexico was in 3 days and I realized my passport had expired. Fast Passport Center got me a new one in 30 hours. Saved our entire family vacation.",
      service: "Emergency Passport",
    },
    {
      name: "Mark L.",
      location: "Laguna, CA",
      rating: 5,
      text: "Needed to renew for a business trip to Vancouver. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Great service for Elk Grove residents.",
      service: "Passport Renewal",
    },
    {
      name: "Jennifer D.",
      location: "Galt, CA",
      rating: 5,
      text: "Lost my passport the week before my wedding in Jamaica. They walked me through the DS-64 process and got me a replacement in 2 days. Incredible service for the Sacramento area.",
      service: "Lost Passport",
    },
    {
      name: "Robert W.",
      location: "Sacramento, CA",
      rating: 5,
      text: "As a state employee who travels internationally, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable for capital region travelers.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "elk-grove-1",
      name: "Elk Grove Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "8850 Williamson Dr",
      city: "Elk Grove",
      state: "CA",
      zip: "95624",
      phone: "(916) 685-6123",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.8 miles from downtown Elk Grove",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "elk-grove-2",
      name: "Sacramento Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2000 Royal Oaks Dr",
      city: "Sacramento",
      state: "CA",
      zip: "95813",
      phone: "(916) 263-7241",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Sacramento",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "elk-grove-3",
      name: "Galt Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "600 N Lincoln Way",
      city: "Galt",
      state: "CA",
      zip: "95632",
      phone: "(209) 745-2661",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.6 miles from downtown Galt",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for southern Sacramento County residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "elk-grove-4",
      name: "Rancho Cordova Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "11000 Olson Dr",
      city: "Rancho Cordova",
      state: "CA",
      zip: "95670",
      phone: "(916) 638-4601",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.8 miles from downtown Rancho Cordova",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Rancho Cordova and eastern Sacramento County residents. Call to confirm passport hours.",
      icon: "ri-mail-line",
    },
    {
      id: "elk-grove-5",
      name: "Folsom Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1300 East Bidwell St",
      city: "Folsom",
      state: "CA",
      zip: "95630",
      phone: "(916) 985-4845",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.0 miles from Folsom Lake College",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for eastern Sacramento County. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "elk-grove-6",
      name: "Sacramento County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "600 8th St",
      city: "Sacramento",
      state: "CA",
      zip: "95814",
      phone: "(916) 874-6334",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.2 miles from the Sacramento County Courthouse",
      website: "https://www.saccounty.gov/",
      websiteLabel: "Sacramento County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown San Francisco", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Sacramento area photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Sacramento County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Elk Grove applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the San Francisco regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Sacramento County families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Capital region travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Elk Grove." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Elk Grove address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Elk Grove before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Elk Grove for travelers with urgent international flights. Our registered couriers achieve 24–48 hour turnaround for most Sacramento area travelers." },
    { q: "How fast can I renew my passport in Elk Grove?", a: "Fast Passport Center offers fast passport renewal in Elk Grove in as little as 24–48 hours through our emergency courier service." },
    { q: "What if my passport expires before my trip?", a: "If your passport expires before your trip, contact Fast Passport Center immediately. We can expedite your passport renewal in Elk Grove in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Elk Grove?", a: "Same-day passport appointments are only available at the SF Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Elk Grove residents." },
    { q: "Can I expedite a child passport in Elk Grove?", a: "Yes. Fast Passport Center expedites child passports in Elk Grove for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Elk Grove with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in Elk Grove?",
      a: "With Fast Passport Center's emergency courier service, Elk Grove travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to drive to the San Francisco Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Elk Grove?",
      a: "Popular options include the Elk Grove Post Office (95624), Sacramento Main Post Office (95813), Galt Post Office (95632), and Sacramento County Clerk (95814). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Elk Grove?",
      a: "Same-day passports are only available at the San Francisco Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live in Galt, Lodi, or nearby communities?",
      a: "We serve all of southern Sacramento County and northern San Joaquin County including Galt, Lodi, and Woodbridge. Our overnight shipping labels work from any UPS location, so your location within the region doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Elk Grove?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Elk Grove?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Elk Grove and Sacramento offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Elk Grove?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Elk Grove?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Sacramento County families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Elk Grove", address: "8501 Elk Grove Blvd, Elk Grove, CA 95624", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Elk Grove", address: "9200 Elk Grove Blvd, Elk Grove, CA 95624", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Sacramento", address: "2609 Capitol Ave, Sacramento, CA 95816", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Laguna", address: "7419 Laguna Blvd, Elk Grove, CA 95758", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Galt", address: "10470 Twin Cities Rd, Galt, CA 95632", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Lodi", address: "2355 W Kettleman Ln, Lodi, CA 95242", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Elk Grove", address: "9120 Elk Grove Blvd, Elk Grove, CA 95624", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Elk Grove", address: "7400 Elk Grove Blvd, Elk Grove, CA 95758", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Sacramento South",
      address: "7775 La Mancha Way, Sacramento, CA 95823",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required. Closest to Elk Grove.",
      featured: true,
    },
    {
      name: "DMV — Sacramento Broadway",
      address: "4700 Broadway, Sacramento, CA 95820",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Lodi",
      address: "1222 S Pleasant Ave, Lodi, CA 95240",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Folsom",
      address: "323 E Bidwell St, Folsom, CA 95630",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for eastern Sacramento County residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "SMF",
      name: "Sacramento International Airport",
      distance: "30 miles from Elk Grove",
      address: "6900 Airport Blvd, Sacramento, CA 95837",
      note: "The primary international airport for the Sacramento metro. Flights to Mexico, Canada, and connections worldwide.",
      flightUrl: "https://www.sacramento.aero/smf/",
      flightLabel: "Sacramento Airport",
      featured: true,
    },
    {
      code: "OAK",
      name: "Oakland International Airport",
      distance: "85 miles from Elk Grove",
      address: "1 Airport Dr, Oakland, CA 94621",
      note: "Alternative Bay Area airport with domestic and international flights to Mexico and beyond.",
      flightUrl: "https://www.oaklandairport.com/",
      flightLabel: "Oakland Airport",
      featured: false,
    },
    {
      code: "SFO",
      name: "San Francisco International Airport",
      distance: "95 miles from Elk Grove",
      address: "San Francisco, CA 94128",
      note: "Major international hub for long-haul flights to Asia, Europe, and worldwide destinations.",
      flightUrl: "https://www.flysfo.com/",
      flightLabel: "SFO Flight Info",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    {
      label: "California Dept. of Public Health (Vital Records)",
      description: "State-issued birth and death certificates for all California counties",
      url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx",
    },
    {
      label: "Sacramento County Clerk-Recorder (Birth, Death, Marriage Records)",
      description: "Birth certificates, death certificates, and marriage records for Sacramento County",
      url: "https://www.saccounty.gov/Government/ClerkRecorder/",
    },
    {
      label: "Sacramento County Superior Court (Divorce Records)",
      description: "Divorce decrees and court records for Sacramento County",
      url: "https://www.saccourt.ca.gov/",
    },
  ],
};